import React, { useState, useEffect, useLayoutEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import authService from "./services/authService";
import planService from "./services/planService";
import { PlanProvider } from "./context/PlanContext";
import Header from "./layout/header";
import Footer from "./layout/footer";
import Login from "./components/login";
import ProductsList from "./components/list";
import Products from "./components/products";
import UserList from "./components/user";
import UserView from "./components/user_view";
import UserEdit from "./components/user_edit";
import GiftList from "./components/gift";
import AddGift from "./components/add_gift";
import GreetingList from "./components/greeting";
import AddGreetingCard from "./components/add_greeting";
import Category from "./components/category";
import AddCategory from "./components/add_category";
import Ordermanagement from "./components/ordermanagement";
import Ordermanagementview from "./components/ordermanagement_view";
import Signup from "./components/signup";
import StoreReady from "./components/StoreReady";
// import BusinessList from "./components/business";
import WebStoreSettings from "./components/WebStoreSettings";
import ProductManagement from "./components/productmanagement";
import ShippingSettings from "./components/ShippingSettings";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Payment from "./components/Payment";
import CreatePaymentAccount from "./components/CreatePaymentAccount";
import ForgotPassword from "./components/ForgotPassword";
import ResetPassword from "./components/ResetPassword";
import Profile from "./components/Profile";
import GSTManagementBusiness from "./components/GSTManagementBusiness";
import TermsCondition from "./components/TermsCondition";
import DomainSettings from "./components/DomainSettings";
import Plan from "./components/plans";
import Subscription from './components/subscription';
import { capturePendingPlan, afterLoginPath } from './utils/pendingPlan';
import InvoiceManager from './components/InvoiceManager';
import SubscriptionInvoiceManager from './components/SubscriptionInvoiceManager';
import UnifiedInvoiceManager from './components/UnifiedInvoiceManager';
import EbooksManagement from './components/EbooksManagement';
import AnalyticsPage from './pages/AnalyticsPage';
import MetaPage from './pages/MetaPage';
import AnalyticsHubPage from './pages/AnalyticsHubPage';



// Simple function to track user events with Smartlook
const trackUserEvent = (eventName, properties = {}) => {
  if (window.smartlook) {
    window.smartlook('track', eventName, properties);
  }
};

const identifyUser = (userId, properties = {}) => {
  if (window.smartlook) {
    window.smartlook('identify', userId, properties);
  }
};

const App = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(null);
  // The plan picked on mytamilkart.com (?plan=), kept until checkout opens for it
  useState(capturePendingPlan);
  const [jwtInitialized, setJwtInitialized] = useState(false);

  // 🔒 JWT AUTHENTICATION TEST - Initialize token on app startup
  useEffect(() => {
    const initializeJWT = async () => {
      try {
        console.log('[APP] 🔒 Initializing JWT authentication (TEST MODE)...');
        await authService.initializeToken();
        setJwtInitialized(true);
        console.log('[APP] ✅ JWT authentication initialized successfully');
      } catch (error) {
        console.error('[APP] ❌ JWT initialization failed:', error);
        console.log('[APP] ⚠️ Continuing without JWT - using fallback authentication');
        setJwtInitialized(true); // Continue anyway with fallback
      }
    };

    initializeJWT();
  }, []);

  // 📊 FETCH PLANS FROM API - Initialize plan data on app startup
  useEffect(() => {
    const initializePlans = async () => {
      try {
        console.log('[APP] 📊 Fetching plans from API...');
        await planService.fetchPlansFromAPI();
        console.log('[APP] ✅ Plans initialized successfully');
      } catch (error) {
        console.error('[APP] ❌ Failed to fetch plans:', error);
        console.log('[APP] ⚠️ Using fallback hardcoded plan limits');
      }
    };

    initializePlans();
  }, []);

  useEffect(() => {
    const isAuthenticated = checkUserAuthentication();
    setIsLoggedIn(isAuthenticated);
  }, []);

  // Track user authentication events
  useEffect(() => {
    if (isLoggedIn) {
      const userId = window.localStorage.getItem("user_id");
      if (userId) {
        identifyUser(userId, {
          authenticated: true,
          loginTime: new Date().toISOString(),
        });
        trackUserEvent('user_login', {
          userId: userId,
          timestamp: new Date().toISOString(),
        });
      }
    } else if (isLoggedIn === false) {
      trackUserEvent('user_logout', {
        timestamp: new Date().toISOString(),
      });
    }
  }, [isLoggedIn]);

  // Function to check authentication
  const checkUserAuthentication = () => {
    const token = window.localStorage.getItem("user_id");
    return !!token; // Returns true if token exists, otherwise false
  };

  // Show loading screen until JWT is initialized AND authentication is checked
  if (!jwtInitialized || isLoggedIn === null) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
       
      }}>
        <div style={{ textAlign: 'center' }}>
          {/* Spinner */}
          <div style={{
            width: '50px',
            height: '50px',
            margin: '0 auto 20px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #1E5B3A',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite'
          }}></div>
          
          {/* Loading Text */}
          <h3 style={{ 
            color: '#1E5B3A', 
            fontSize: '18px', 
            fontWeight: '600',
            margin: '0 0 8px 0' 
          }}>
            {!jwtInitialized ? 'Initializing...' : 'Loading...'}
          </h3>
          
          {/* Subtext */}
          <p style={{ 
            color: '#999', 
            fontSize: '13px', 
            margin: 0 
          }}>
            {!jwtInitialized ? 'Setting up secure connection' : 'Please wait'}
          </p>
        </div>
        
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <PlanProvider>
        <BodyClassManager isLoggedIn={isLoggedIn} />
        <Routes>
        <Route path="/signup" element={!isLoggedIn ? <Signup setIsLoggedIn={setIsLoggedIn} /> : <Navigate to={afterLoginPath("/gift")} />} />

        <Route path="/list" element={isLoggedIn ? <Ordermanagementview /> : <Navigate to="/login" />} />
        <Route path="/products" element={<Products />} />

        <Route path="/login" element={!isLoggedIn ? <Login setIsLoggedIn={setIsLoggedIn} /> : <Navigate to={afterLoginPath("/gift")} />} />


        <Route path="/" element={isLoggedIn ? <GiftList /> : <Signup setIsLoggedIn={setIsLoggedIn} />} />
        <Route path="/users" element={isLoggedIn ? <UserList /> : <Login setIsLoggedIn={setIsLoggedIn} />} />
        <Route path="/user_view" element={isLoggedIn ? <UserView /> : <Login setIsLoggedIn={setIsLoggedIn} />} />
        <Route path="/user_edit" element={isLoggedIn ? <UserEdit /> : <Login setIsLoggedIn={setIsLoggedIn} />} />

        {/* <Route path="/businesses" element={isLoggedIn ? <BusinessList /> : <Login setIsLoggedIn={setIsLoggedIn} />} /> */}

        <Route path="/gift" element={isLoggedIn ? <GiftList /> : <Login setIsLoggedIn={setIsLoggedIn} />} />
        <Route path="/gift_add" element={isLoggedIn ? <AddGift /> : <Login setIsLoggedIn={setIsLoggedIn} />} />
        <Route path="/gift_edit" element={isLoggedIn ? <AddGift /> : <Login setIsLoggedIn={setIsLoggedIn} />} />
        <Route path="/add_gift" element={isLoggedIn ? <AddGift /> : <Login setIsLoggedIn={setIsLoggedIn} />} />
        <Route path="/ebooks" element={isLoggedIn ? <EbooksManagement /> : <Login setIsLoggedIn={setIsLoggedIn} />} />


        {/* <Route path="/greetingcard" element={isLoggedIn ? <GreetingList /> : <Login setIsLoggedIn={setIsLoggedIn} />} />
        <Route path="/greetingcard_add" element={isLoggedIn ? <AddGreetingCard /> : <Login setIsLoggedIn={setIsLoggedIn} />} />
        <Route path="/greetingcard_edit" element={isLoggedIn ? <AddGreetingCard /> : <Login setIsLoggedIn={setIsLoggedIn} />} /> */}


        <Route path="/category" element={isLoggedIn ? <Category /> : <Login setIsLoggedIn={setIsLoggedIn} />} />
        <Route path="/category_add" element={isLoggedIn ? <AddCategory /> : <Login setIsLoggedIn={setIsLoggedIn} />} />
        <Route path="/category_edit" element={isLoggedIn ? <AddCategory /> : <Login setIsLoggedIn={setIsLoggedIn} />} />


        <Route path="/ordermanagement" element={isLoggedIn ? <Ordermanagement /> : <Login setIsLoggedIn={setIsLoggedIn} />} />
        <Route path="/ordermanagement_view" element={isLoggedIn ? <Ordermanagementview /> : <Login setIsLoggedIn={setIsLoggedIn} />} />

        <Route path="/webstore_settings" element={isLoggedIn ? <WebStoreSettings /> : <Login setIsLoggedIn={setIsLoggedIn} />} />
        <Route path="/payment" element={isLoggedIn ? <Payment /> : <Login setIsLoggedIn={setIsLoggedIn} />} />
        <Route path="/create-payment" element={isLoggedIn ? <CreatePaymentAccount /> : <Login setIsLoggedIn={setIsLoggedIn} />} />
        <Route path="/forgot_password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/terms-condition" element={<TermsCondition />} />
        <Route path="/profile" element={isLoggedIn ? <Profile /> : <Login setIsLoggedIn={setIsLoggedIn} />} />
        <Route path="/gst-management" element={isLoggedIn ? <GSTManagementBusiness /> : <Login setIsLoggedIn={setIsLoggedIn} />} />
        <Route path="/productmanagement" element={isLoggedIn ? <ProductManagement /> : <Login setIsLoggedIn={setIsLoggedIn} />} />
        <Route path="/domain-settings" element={isLoggedIn ? <DomainSettings /> : <Login setIsLoggedIn={setIsLoggedIn} />} />
        <Route path="/shipping-settings" element={isLoggedIn ? <ShippingSettings /> : <Login setIsLoggedIn={setIsLoggedIn} />} />
        <Route path="/purchase-plan" element={isLoggedIn ? <Plan /> : <Login setIsLoggedIn={setIsLoggedIn} />} />
        <Route path="/my-invoices" element={isLoggedIn ? <UnifiedInvoiceManager /> : <Login setIsLoggedIn={setIsLoggedIn} />} />
        <Route path="/invoices" element={isLoggedIn ? <InvoiceManager /> : <Login setIsLoggedIn={setIsLoggedIn} />} />
        <Route path="/subscription-invoices" element={isLoggedIn ? <SubscriptionInvoiceManager /> : <Login setIsLoggedIn={setIsLoggedIn} />} />
        <Route path="/analytics-hub" element={isLoggedIn ? <AnalyticsHubPage /> : <Login setIsLoggedIn={setIsLoggedIn} />} />
        <Route path="/analytics" element={isLoggedIn ? <AnalyticsPage /> : <Login setIsLoggedIn={setIsLoggedIn} />} />
        <Route path="/meta" element={isLoggedIn ? <MetaPage /> : <Login setIsLoggedIn={setIsLoggedIn} />} />
      
<Route path="/subscription" element={<Subscription />} />


      </Routes>
      {/* "Your store is ready!" popup over the dashboard, once after signup */}
      {isLoggedIn && <StoreReady />}
      <ToastContainer position="top-right" autoClose={3000} />
      </PlanProvider>
    </BrowserRouter>
  );
};

// Manage body class changes based on the authentication state
const BodyClassManager = ({ isLoggedIn }) => {
  const location = useLocation();

  // Before the first paint, so the dashboard theme (and the inline var(--…) colours
  // that need body.mtk-admin) is there from the first frame
  useLayoutEffect(() => {
    if (!isLoggedIn) {
      document.body.classList.add('login-page');
      document.body.classList.remove('sidebar-mini', 'mtk-admin');
    } else {
      document.body.classList.remove('login-page');
      // mtk-admin: the dashboard theme (assets/css/AdminTheme.css)
      document.body.classList.add('sidebar-mini', 'mtk-admin');
    }
  }, [location.pathname, isLoggedIn]);

  return null;
};

export default App;
